import React from 'react';
import { ArrowDownIcon, ArrowLeftRightIcon, ArrowRightIcon } from 'lucide-react';
import { IMAGES } from '../data/site';
import { QuoteForm } from './QuoteForm';
export type HeroVariant = 'van' | 'storefront';
const HERO_ALT = 'Concept image of two Kosin’s Glass service vans at a commercial building, one with its cargo door open';
const STOREFRONT_ALT = 'The Kosin’s Glass brick storefront with its tall freestanding red sign and the company’s van and service trucks parked in front';

/* Desktop-only readability shading for the default van photo: the corrected
   building sits lower, so the copy now falls on open sky. Neutral black, fading
   out above the vehicle roofs and toward the form side. */
const VAN_DESKTOP_SCRIM: React.CSSProperties = {
  backgroundImage: 'linear-gradient(180deg, rgba(0,0,0,0.66) 0%, rgba(0,0,0,0.64) 58%, rgba(0,0,0,0.48) 78%, rgba(0,0,0,0) 100%)'
};

/* Mobile/tablet shading for the default van photo: covers the longest
   two-line CTA at 320px and fades out above the vehicle logos. */
const VAN_MOBILE_SCRIM: React.CSSProperties = {
  backgroundImage: 'linear-gradient(180deg, rgba(0,0,0,0.67) 0%, rgba(0,0,0,0.67) 84%, rgba(0,0,0,0) 100%)'
};

/* Neutral black scrim for the brighter storefront photo: strongest behind the
   copy, easing off smoothly over the lower vehicle and road area. */
const STOREFRONT_SCRIM: React.CSSProperties = {
  backgroundImage: 'linear-gradient(180deg, rgba(0,0,0,0.76) 0%, rgba(0,0,0,0.74) 40%, rgba(0,0,0,0.60) 62%, rgba(0,0,0,0.42) 82%, rgba(0,0,0,0.30) 100%), linear-gradient(90deg, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.08) 45%, rgba(0,0,0,0) 75%)'
};
const PHOTO_CLASS = 'absolute inset-x-0 top-0 h-[600px] w-full sm:h-[720px] lg:h-[620px] xl:h-full';
function buildCompareHref(variant: HeroVariant): string {
  if (typeof window === 'undefined') {
    return variant === 'storefront' ? '/' : '/?hero=storefront';
  }
  const params = new URLSearchParams(window.location.search);
  if (variant === 'storefront') {
    params.delete('hero');
  } else {
    params.set('hero', 'storefront');
  }
  const query = params.toString();
  return `${window.location.pathname}${query ? `?${query}` : ''}`;
}
interface HeroProps {
  variant?: HeroVariant;
}
export function Hero({
  variant = 'van'
}: HeroProps) {
  const isStorefront = variant === 'storefront';
  const compareHref = buildCompareHref(variant);
  const compareLabel = isStorefront ? 'View Van Photo Version' : 'View Storefront Photo Version';
  return <section aria-labelledby="hero-heading" className="relative isolate w-full bg-kosin-mist xl:bg-kosin-charcoal" data-id="element-160" data-mp="161ja702cc7dk">
      {isStorefront ? <img src={IMAGES.heroStorefront} alt={STOREFRONT_ALT} width={1672} height={941} className={`${PHOTO_CLASS} object-cover object-center [filter:saturate(0.72)]`} data-id="element-161" data-mp="114kkctppmml6" /> : <picture data-id="element-162" data-mp="3hr3rdunnk95a">
          <source media="(min-width: 1024px)" srcSet={IMAGES.heroWide} width={2172} height={724} data-id="element-163" data-mp="3hqkbejhkbutw" />
          <img src={IMAGES.heroMobile} alt={HERO_ALT} width={1086} height={1448} className={`${PHOTO_CLASS} object-cover object-bottom lg:object-left-bottom`} data-id="element-164" data-mp="21amwfav61umv" />
        </picture>}

      {isStorefront ? <div aria-hidden="true" className={PHOTO_CLASS} style={STOREFRONT_SCRIM} data-id="element-165" data-mp="2b2kbwtt7j8re" /> : <>
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[440px] w-full sm:h-[360px] lg:hidden" style={VAN_MOBILE_SCRIM} data-id="element-166" data-mp="2kyvlvd5brmio" />
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 hidden h-[360px] lg:block" style={VAN_DESKTOP_SCRIM} data-id="element-167" data-mp="2tzboyli5orn0" />
        </>}

      <div className="relative mx-auto max-w-shell px-[18px] sm:px-6 xl:grid xl:min-h-[620px] xl:grid-cols-[minmax(0,1fr)_400px] xl:items-start xl:gap-9 xl:py-6" data-id="element-168" data-mp="zl0zqbxnfvgp">
        <div className="min-h-[600px] pt-6 sm:min-h-[720px] sm:pt-8 lg:min-h-[620px] xl:min-h-0 xl:pt-0" data-id="element-169" data-mp="4dgw6qh8nf60">
          <p className={`flex items-center gap-3 text-[16px] font-bold ${isStorefront ? 'text-white' : 'text-kosin-ondark'}`} data-id="element-170" data-mp="3vk1ixy09g0x8">
            <span className="h-[3px] w-8 shrink-0 bg-kosin-ondark" aria-hidden="true" data-id="element-171" data-mp="9b8npjdz0z9o" />
            Kosin’s Glass
          </p>

          <h1 id="hero-heading" className={`mt-2 max-w-[700px] text-[30px] font-bold leading-[1.1] text-white sm:text-[38px] xl:text-[44px] ${isStorefront ? '[text-shadow:0_1px_6px_rgba(0,0,0,0.45)]' : '[text-shadow:0_2px_4px_rgba(0,0,0,0.9)]'}`} data-id="element-172" data-mp="3cs8ri80uanrm">
            Glass Repair, Replacement &amp; Custom Glass
          </h1>

          <p className="mt-3 max-w-[560px] text-[16px] leading-[1.5] text-white sm:text-[17px] [text-shadow:0_1px_12px_rgba(0,0,0,0.6)]" data-id="element-173" data-mp="2tzwn7fqmlceu">
            Family-owned since 2000, serving Southeast Michigan’s vehicles,
            homes and businesses.
          </p>

          <p className="mt-3 text-[14px] leading-5 text-white sm:text-[15px] [text-shadow:0_1px_12px_rgba(0,0,0,0.6)]" data-id="element-174" data-mp="zkzo9crnvlhz">
            <span className="block font-semibold sm:inline" data-id="element-175" data-mp="14klzxhructoo">Three Convenient Locations</span>
            <span className="mx-2 hidden text-white/60 sm:inline" aria-hidden="true" data-id="element-176" data-mp="1s8vf40ujuj6u">
              |
            </span>
            Howell • Ann Arbor • Novi
          </p>

          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-[14px] font-semibold text-white" data-id="element-177" data-mp="1j8qlqum6xlbo">
            <a href="#hero-quote" className="focus-light inline-flex min-h-8 items-center gap-2 underline underline-offset-4 xl:hidden" data-id="element-178" data-mp="1n9scso5yer2k">
              Get A Free Quote
              <ArrowDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-179" data-mp="bvetzxq251te" />
            </a>
            <a href="#services" className="focus-light inline-flex min-h-8 items-center gap-2 underline underline-offset-4" data-id="element-180" data-mp="2ehaqfpta2m7h">
              Explore Our Services
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-181" data-mp="24rheuot4y738" />
            </a>
          </div>
        </div>

        <div id="hero-quote" className="pb-8 pt-5 xl:w-[400px] xl:justify-self-end xl:py-0" data-id="element-182" data-mp="1ztfwk63jabst">
          <QuoteForm idPrefix="hero" data-id="element-183" data-mp="1vrlze7sjrpxe" />
        </div>

        <a href={compareHref} className="focus-light absolute left-[18px] top-[544px] z-10 inline-flex items-center gap-2 rounded bg-black/60 px-3 py-1.5 text-[13px] font-semibold text-white underline underline-offset-4 hover:bg-black/75 transition-colors duration-150 ease-kosin sm:left-6 sm:top-[664px] lg:top-[564px] xl:bottom-5 xl:top-auto" data-id="element-184" data-mp="1vozqa3q0d2d2">
          <ArrowLeftRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-185" data-mp="3g5mh9ddpa8wg" />
          {compareLabel}
        </a>
      </div>
    </section>;
}